import React, { useContext, useState } from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import PersonIcon from '@mui/icons-material/Person';
import axios from '../../../sharedModules/utils/axios.utils';
import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';

interface EditableAmountCellProps {
  lineItem: any;
  vendorInvoiceId: string;
  disabled?: boolean;
  onSaved: () => void;
}

const formatAmount = (value: any): string => {
  if (value === null || value === undefined || value === '') return 'N/A';
  const parsed = parseFloat(value.toString());
  if (Number.isNaN(parsed)) return 'N/A';
  return `$${parsed.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Mirrors the server rule: anything parseable as a number except blank and zero.
// Negatives are valid because vendor statements carry credit lines.
const parseInput = (raw: string): number | null => {
  const cleaned = raw.trim().replace(/[$,]/g, '');
  if (!cleaned) return null;
  const parsed = Number(cleaned);
  if (Number.isNaN(parsed) || parsed === 0) return null;
  return parsed;
};

const EditableAmountCell: React.FC<EditableAmountCellProps> = ({ lineItem, vendorInvoiceId, disabled, onSaved }) => {
  const { showErrorSnackBar, showSuccessSnackBar } = useContext(SnackBarContext);
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  const edited = !!lineItem.amount_edited_at;
  const isValid = parseInput(value) !== null;

  const startEditing = () => {
    if (disabled || saving) return;
    setValue(lineItem.amount != null ? parseFloat(lineItem.amount.toString()).toFixed(2) : '');
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditing(false);
    setValue('');
  };

  const save = async () => {
    const parsed = parseInput(value);
    if (parsed === null) return;

    // Skip the round trip when the value is unchanged — avoids stamping a
    // spurious "edited by human" marker on a cell the user only tabbed through.
    if (lineItem.amount != null && parsed === parseFloat(lineItem.amount.toString())) {
      cancelEditing();
      return;
    }

    setSaving(true);
    try {
      await axios.patch(`/vendors/vendor_invoices/${vendorInvoiceId}/line_items/${lineItem.id}/update_amount`, {
        amount: parsed,
      });
      showSuccessSnackBar('Amount updated');
      setEditing(false);
      setValue('');
      onSaved();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.errors?.[0] || 'Failed to update amount';
      showErrorSnackBar(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      save();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelEditing();
    }
  };

  if (editing) {
    return (
      <Box display="flex" alignItems="center" gap={0.5}>
        <TextField
          autoFocus
          size="small"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          error={!isValid}
          helperText={!isValid ? 'Enter a non-zero number' : undefined}
          disabled={saving}
          sx={{ width: 120 }}
          inputProps={{ inputMode: 'decimal' }}
        />
        {saving ? (
          <CircularProgress size={18} />
        ) : (
          <>
            <Tooltip title="Save">
              <IconButton size="small" onClick={save} disabled={!isValid}>
                <CheckIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Cancel">
              <IconButton size="small" onClick={cancelEditing}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Box>
    );
  }

  const editedTooltip = [
    `Edited by ${lineItem.amount_edited_by?.full_name || 'a user'}`,
    lineItem.amount_edited_at
      ? ` on ${new Date(lineItem.amount_edited_at).toLocaleString('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })}`
      : '',
    lineItem.original_amount != null ? ` — OCR read ${formatAmount(lineItem.original_amount)}` : '',
  ].join('');

  return (
    <Box
      display="flex"
      alignItems="center"
      gap={0.5}
      onDoubleClick={startEditing}
      sx={{ '&:hover .amount-edit-affordance': { opacity: 1 } }}
    >
      <Typography variant="body2">{formatAmount(lineItem.amount)}</Typography>

      {edited && (
        <Tooltip title={editedTooltip}>
          <PersonIcon fontSize="small" color="primary" />
        </Tooltip>
      )}

      {!disabled && (
        <Tooltip title="Edit amount">
          <IconButton
            size="small"
            className="amount-edit-affordance"
            onClick={startEditing}
            sx={{ opacity: 0, transition: 'opacity 120ms' }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

export default EditableAmountCell;
